export interface IPreview {
    title: string;
    subtitle: string;
    content: string[];
    imageUrl: string;
}

export interface ISpecialProduct {
    title: string;
    content: string[];
    imageUrl: string;
}

export interface ICollaboration {
    title: string;
    content: string[];
}

export interface IProjectInfo {
    title: string;
    iconUrl: string;
    project: {
        title: string;
        subtitle: string;
    };
    info: {
        blocks: Array<{ title: string, subtitle: string }>
    };
    solutions: {
        title: string;
        content: string[];
    };
}

export interface IDevelopmentChallenges {
    title: string;
    accordionItems: FAQAccordionItems[];
}

interface FAQAccordionItems {
    question: string;
    answers: string[];
}

interface IconBlock {
    iconUrl: string;
    iconName: string;
    iconWidth: number;
    iconHeight: number;
    containerWidth?: number;
    containerHeight?: number;
}

export interface ITechnologyStack {
    title: string;
    blocks: Array<{ title: string, icons: IconBlock[] }>
}

interface IKeyFeaturePoint {
    pointNumber: number;
    title: string;
    content: string;
}

export interface IKeyFeatures {
    title: string;
    subtitle: string;
    blocks: Array<{ imageUrl: string; points: IKeyFeaturePoint[] }>;
}

export interface IOurResults {
    title: string;
    subtitle: string;
    imageUrl: string;
    list: Array<{ text: string, imageUrl: string }>
}

export interface ILevel01State {
    preview: IPreview;
    specialProduct: ISpecialProduct;
    collaboration: ICollaboration;
    projectInfo: IProjectInfo;
    developmentChallenges: IDevelopmentChallenges;
    technologyStack: ITechnologyStack;
    keyFeatures: IKeyFeatures;
    ourResults: IOurResults;
}

const imgBasePath = '/level01-images';
const iconsBasePath = `${imgBasePath}/icons`;

export const Level01State: ILevel01State = {
    preview: {
        title: 'Level01',
        subtitle: 'Protected and fast p2p trading space for cryptocurrency transactions',
        content: [
            'Level01 is a secure peer-to-peer decentralized finance platform. More specifically, it works with the cryptocurrency market and is based on the Ethereum blockchain.',
            'With Level01, users create and manage their own crypto wallets and store coins in them. The main idea was to let users freely and fairly trade options on the crypto exchange using a well-protected trading environment.'
        ],
        imageUrl: `${imgBasePath}/preview.png`,
    },
    specialProduct: {
        title: 'What makes the product special?',
        content: [
            'Level01 applies AI prediction to forecast percentage of success for a particular option;',
            'Multitude of markets provides users with the opportunity to trade both Level01-x and Ethereum tokens;',
            'Crypto coins in trading-intended wallets are stored separately (the so-called Tradeline balance);',
            'Level01 has all the advantages that decentralized systems have over traditional ones paying attention to the security and simplification.'
        ],
        imageUrl: `${imgBasePath}/special-product.png`
    },
    collaboration: {
        title: 'Our Collaboration & Tasks',
        content: [
            'Initial idea was to build a secure and easy-to-use p2p trading solution. Client was to create an MVP for an Android app and target Android users only to get the next round of investments.',
            'During the <strong>Discovery phase</strong> we\'ve analyzed all goals, reviewed technical challenges and prepared a solid roadmap to cover both iOS and Android apps’ users. As a result we’ve found a way to make a cross platform frontend solution.',
            'Still being focused on Android, we’ve built a foundation to implement Company plans where after getting the investment, Level01 could get to the iOS market as fast as possible.'
        ]
    },
    projectInfo: {
        title: 'Project info',
        project: {
            title: 'P2P trading app development',
            subtitle: 'Project'
        },
        info: {
            blocks: [
                {
                    title: '4-6 developers',
                    subtitle: 'Team size'
                },
                {
                    title: '12+ month',
                    subtitle: 'Project duration'
                }
            ]
        },
        solutions: {
            title: 'Solutions provided',
            content: [
                'Discovery phase including competitors analysis, product architecture, prototype, technical research',
                'P2P Android app published in Store',
                'Agilie based team collaboration based on the flexible SDLC',
                'Agreed communication protocol to keep Multitude of Markets'
            ],
        },
        iconUrl: `${iconsBasePath}/checked.png`
    },
    developmentChallenges: {
        title: 'Development Challenges to Overcome',
        accordionItems: [
            {
                question: 'High-speed data stream',
                answers: ['Trading exchange information requires constant and heavy data transfer between the app and exchange software. This data includes exchange rates, smart contracts details, overall health & history of trading. Level01 incorporates such communication protocols as Restful APIs, socket connection and cloud notifications. Our challenge was to create a Service to synchronize the data and let users receive real time exchange rates. Another challenge was to meet the latency benchmark for efficient and fast trading via data structure optimisation and microservice architecture.'],
            },
            {
                question: 'Security issues',
                answers: ['Expected target audience included both experienced and newbie crypto traders. To let them onboarding we’ve decided to keep the wallets as custodial. We’ve treated security as the first priority. We added a couple of protection layers as restricted access to the private keys storage over VPN, dedicated IP address, using extra AWS security options.']
            },
            {
                question: 'Multitude of Markets',
                answers: ['Level01 uses a couple trading data exchange sources. One of the challenges was to keep a constant integration channel with each of them. The second challenge was related to keeping the trading data actual and up to date. To solve both challenges we applied a distributed microservice approach. Each market’s integration was processed via its own microservice. A separate Service was responsible for data aggregation, optimisation and sharing with the overall platform.']
            },
            {
                question: 'Smart contracts',
                answers: ['As part of Option trading we’ve developed specific smart contracts to cover base trading functionality. One of the challenges was related to adding AI insights during contract creation. We kept AI prediction data inside of the smart contract for future analysis and model improvement.']
            },
            {
                question: 'Finding optimal cross-platform dev solutions',
                answers: ['Due to the decision of having a cross platform solution we’ve paid attention to application performance. In the initial stage we’ve decided to keep component based app structure with an idea to reuse the components over different scenarios. This allowed us to improve the performance during the delivery process - it’s especially critical for experienced traders who are making a lot of contracts simultaneously.']
            }
        ]
    },
    technologyStack: {
        title: 'Technology Stack Used',
        blocks: [
            {
                title: 'Cross-platform development',
                icons: [
                    {
                        iconName: 'React Native',
                        iconUrl: `${iconsBasePath}/react.png`,
                        iconWidth: 39,
                        iconHeight: 40
                    },
                    {
                        iconName: 'Swift',
                        iconUrl: `${iconsBasePath}/swift.png`,
                        iconWidth: 40,
                        iconHeight: 40
                    },
                    {
                        iconName: '.Net',
                        iconUrl: `${iconsBasePath}/dotnet.png`,
                        iconWidth: 40,
                        iconHeight: 40
                    },
                    {
                        iconName: 'Kotlin',
                        iconUrl: `${iconsBasePath}/kotlin.png`,
                        iconWidth: 40,
                        iconHeight: 40
                    }
                ]
            },
            {
                title: 'Blockchain & smart contracts',
                icons: [
                    {
                        iconName: 'web3j library',
                        iconUrl: `${iconsBasePath}/web3j.png`,
                        iconWidth: 26,
                        iconHeight: 40
                    },
                    {
                        iconName: 'Solidity',
                        iconUrl: `${iconsBasePath}/solidity.png`,
                        iconWidth: 26,
                        iconHeight: 40
                    }
                ]
            },
            {
                title: 'Currency rate charts',
                icons: [
                    {
                        iconName: 'SciChart',
                        iconUrl: `${iconsBasePath}/scichart.png`,
                        iconWidth: 26,
                        iconHeight: 40
                    }
                ]
            },
            {
                title: 'Real-time data processing',
                icons: [
                    {
                        iconName: 'WebSocket (SocketIO)',
                        iconUrl: `${iconsBasePath}/websocket.png`,
                        iconWidth: 40,
                        iconHeight: 30
                    }
                ]
            },
            {
                title: 'Security',
                icons: [
                    {
                        iconName: 'Oauth2',
                        iconUrl: `${iconsBasePath}/oauth2.png`,
                        iconWidth: 40,
                        iconHeight: 40,
                        containerWidth: 94,
                        containerHeight: 75
                    },
                    {
                        iconName: `Encrypted database`,
                        iconUrl: `${iconsBasePath}/database.png`,
                        iconWidth: 40,
                        iconHeight: 40,
                        containerHeight: 101,
                        containerWidth: 79
                    },
                    {
                        iconName: 'Obfuscation',
                        iconUrl: `${iconsBasePath}/obfuscation.png`,
                        iconWidth: 40,
                        iconHeight: 40,
                        containerWidth: 94,
                        containerHeight: 75
                    },
                ]
            },
            {
                title: 'Cloud solution',
                icons: [
                    {
                        iconName: 'AWS',
                        iconUrl: `${iconsBasePath}/aws.png`,
                        iconHeight: 34,
                        iconWidth: 56
                    }
                ]
            }
        ]
    },
    keyFeatures: {
        title: 'Key features',
        subtitle: 'Check out the main features implemented to ensure app success and competitiveness.',
        blocks: [
            {
                imageUrl: `${imgBasePath}/key-feature-1.png`,
                points: [
                    {
                        title: 'Secure crypto wallet',
                        content: 'Level01 offers a custodial blockchain-based wallet for storing and managing cryptocurrencies. It is easy to use, user-friendly, and (which is the most important thing) well-protected.',
                        pointNumber: 1
                    },
                    {
                        title: 'Options trading',
                        content: 'Level01 provides an opportunity for safe and fair trading in different markets. Users can use Forex, Cryptocurrency, Commodity, create options, etc.',
                        pointNumber: 2
                    },
                    {
                        title: 'Contracts',
                        content: 'Users can create contracts to raise/lower options. Such contracts are needed to avoid foul play to let traders get his share of the profit.',
                        pointNumber: 3
                    }
                ]
            },
            {
                imageUrl: `${imgBasePath}/key-feature-2.png`,
                points: [
                    {
                        title: 'Cryptocurrency charts',
                        content: 'Level01 visualizes data using understandable crypto charts. It makes the process of p2p trading on a crypto exchange much simpler and clearer.',
                        pointNumber: 4
                    },
                    {
                        title: 'AI predictions',
                        content: 'Level01 uses AI prediction over contract creation to help managing options more efficiently and mitigate risks.',
                        pointNumber: 5
                    }
                ]
            }
        ]
    },
    ourResults: {
        title: 'Our results',
        subtitle: 'On the project, we passed through all the steps to achieve all the goals set.',
        imageUrl: `${imgBasePath}/results.png`,
        list: [
            {
                text: 'More than 50,000+ app downloads in Google Play',
                imageUrl: `${iconsBasePath}/checked.png`
            },
            {
                text: 'Well developed and tested scalable solution',
                imageUrl: `${iconsBasePath}/checked.png`
            },
            {
                text: 'Level01 is being actively distributed and popularized in China',
                imageUrl: `${iconsBasePath}/checked.png`
            }
        ],
    }
}
